/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

import { bool, number, object, oneOfType, string } from 'prop-types';
import 'react';

/**
 * A replacement for a group of boolean checks in a render
 * This component takes as it's child an object where the keys are possible states and the values are the possible things to render.
 * It also takes a single value which will be matched against the keys.
 * Optioner will only render the first match.
 * '_' can be used as a default value
 *
 * val may be a primitive, or an object of booleans
 *
 * @param {Object} children
 * @param {*} val
 * @param {string} defaultKey
 * @returns {*}
 *
 * @example
 * // primative as the val
 * <Optioner val={letter}>
 *   { {
 *    a: <h1>Aardvark</h1>,
 *    b: <h1>Bat</h1>,
 *    c: <h1>Cat</h1>,
 *    _: <h1>Yeti</h1>
 *   } }
 * </Optioner>
 *
 * // Object as the val
 * <Optioner val={{ flies: true, swims: false, walks: false }}>
 *   { {
 *    flies: <h1>Dragonfly</h1>,
 *    swims: <h1>Bass</h1>,
 *    walks: <h1>Cat</h1>,
 *    _: <h1>Yeti</h1>
 *   } }
 * </Optioner>
 */
const Optioner = ({ children = {}, val = '_', defaultKey = '_' }) => {
  const key =
    typeof val === 'object'
      ? Object.entries(val)
          .filter(([key, bool]) => bool && key in children)
          .concat([[defaultKey, true]])[0][0]
      : val;

  return key in children
    ? typeof children[key] === 'function'
      ? children[key]()
      : children[key]
    : null;
};

Optioner.propTypes = {
  children: object.isRequired,
  val: oneOfType([string, bool, number, object])
};

export default Optioner;
